/**
 * Pure precedence logic for the web promo-banner sequence.
 *
 * Shows exactly one banner at a time, ordered by completion (NOT by dismissal):
 *   invite:  until a referral code this user created is redeemed
 *   discord: after invite completes, until the Discord-join reward is claimed
 *   (none):  terminal
 *
 * Session dismissal only suppresses the currently-active banner; it never falls
 * through to the next one (the next only appears once the current completes).
 * `isAnonymous` is deliberately NOT a gate: guests see the invite banner too
 * (the invite CTA's own save-account guard drives conversion).
 *
 * No I/O, no store imports. Unit-tested in isolation.
 */

import type { PromoBannerKind } from '@danda/shared';

export interface PromoBannerStatus {
  /** A referral code this user created has been redeemed by a real signup. */
  referralConverted: boolean;
  /** The Discord-join reward has been claimed (rewards.discord === 'credited'). */
  discordClaimed: boolean;
}

export interface PromoBannerDismissals {
  invite: boolean;
  discord: boolean;
}

export function derivePromoBanner(
  status: PromoBannerStatus,
  dismissed: PromoBannerDismissals
): PromoBannerKind | null {
  if (!status.referralConverted) {
    return dismissed.invite ? null : 'invite';
  }
  if (!status.discordClaimed) {
    return dismissed.discord ? null : 'discord';
  }
  return null;
}
